import { useState, useEffect } from "react";
import api from "../utils/apiConfig";

export default function useAccountHoldings(accountNumber) {
  const [holdings, setHoldings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assetAllocation, setAssetAllocation] = useState({});
  const [sectorAllocation, setSectorAllocation] = useState({});
  const [totalMarketValue, setTotalMarketValue] = useState(0);
  const [performanceData, setPerformanceData] = useState({
    labels: [],
    datasets: [],
  });

  useEffect(() => {
    if (!accountNumber) return;
    let isMounted = true;
    setLoading(true);
    async function fetchData() {
      try {
        const res = await api.get(
          `/advisor-portal/holdings?account_number=${accountNumber}`,
        );
        let data = res.data || [];
        // Get all unique symbols
        const symbols = Array.from(
          new Set(data.map((h) => h.symbol).filter(Boolean)),
        );
        // Fetch latest prices
        let priceMap = {};
        let priceHistory = {};
        try {
          const resp = await fetch("/data/all_holdings_weekly_closes.json");
          const json = await resp.json();
          priceHistory = json;
          for (const symbol of symbols) {
            const arr = json[symbol];
            if (arr && arr.length > 0) {
              priceMap[symbol] = arr[arr.length - 1][1];
            }
          }
        } catch {
          priceMap = {};
          priceHistory = {};
        }
        // Calculate market value for each holding
        let total = 0;
        const assetMap = {};
        const sectorMap = {};
        data = data.map((h) => {
          let mv = null;
          const price = priceMap[h.symbol] != null ? priceMap[h.symbol] : null;
          if (price != null && !isNaN(price)) {
            mv = (parseFloat(h.quantity) || 0) * price;
          } else if (h.market_value != null && !isNaN(h.market_value)) {
            mv = parseFloat(h.market_value);
          } else {
            mv = 0;
          }
          total += mv;
          const asset = h.asset_class || "Unknown";
          const sector = h.sector || "Unknown";
          assetMap[asset] = (assetMap[asset] || 0) + mv;
          sectorMap[sector] = (sectorMap[sector] || 0) + mv;
          return { ...h, market_value: mv };
        });
        // Calculate performance data (historical portfolio value)
        let perfData = { labels: [], datasets: [] };
        if (symbols.length > 0 && Object.keys(priceHistory).length > 0) {
          // Use the first symbol's dates as the reference
          const firstSymbol = Object.keys(priceHistory)[0];
          const allDates = priceHistory[firstSymbol].map(([date]) => date);
          // Build a map of shares by symbol (use latest quantity for now)
          const sharesBySymbol = {};
          data.forEach((h) => {
            if (h.symbol)
              sharesBySymbol[h.symbol] = parseFloat(h.quantity) || 0;
          });
          // For each date, sum up value of all holdings
          const values = allDates.map((date) => {
            let total = 0;
            for (const symbol in sharesBySymbol) {
              const arr = priceHistory[symbol];
              if (!arr) continue;
              const entry = arr.find(([d]) => d === date);
              const price = entry ? entry[1] : null;
              const shares = sharesBySymbol[symbol] || 0;
              if (price != null) total += price * shares;
            }
            return total;
          });
          perfData = {
            labels: allDates,
            datasets: [
              {
                label: "Portfolio Value",
                data: values,
                borderColor: "#1E3A8A",
                backgroundColor: "rgba(30,58,138,0.1)",
                fill: true,
                tension: 0.3,
                pointRadius: 3,
                pointHoverRadius: 6,
              },
            ],
          };
        }
        if (!isMounted) return;
        setHoldings(data);
        setAssetAllocation(assetMap);
        setSectorAllocation(sectorMap);
        setTotalMarketValue(total);
        setPerformanceData(perfData);
        setLoading(false);
      } catch (err) {
        if (isMounted) {
          setError(err.message || "Failed to fetch account holdings");
          setLoading(false);
        }
      }
    }
    fetchData();
    return () => {
      isMounted = false;
    };
  }, [accountNumber]);

  return {
    holdings,
    loading,
    error,
    assetAllocation,
    sectorAllocation,
    totalMarketValue,
    performanceData,
  };
}
